import React from "react";

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface Props {
  saldoReisekasse: number;
  totalEinzahlungen: number;
  totalAusgaben: number;
  userTotals: Record<string, { owed: number; paid: number }>;
  users: User[];
}

export default function ReisekassenOverview({
  saldoReisekasse,
  totalEinzahlungen,
  totalAusgaben,
  userTotals,
  users,
}: Props) {
  return (
    <div className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 mb-6">
      <h4 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-4">
        Reisekasse
      </h4>

      {/* 3-col metrics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">Einzahlungen</div>
          <div className="text-sm font-semibold text-emerald-400 tabular-nums">{totalEinzahlungen.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">Ausgaben</div>
          <div className="text-sm font-semibold text-slate-300 tabular-nums">{totalAusgaben.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mb-0.5">Saldo</div>
          <div className={`text-sm font-semibold tabular-nums ${
            saldoReisekasse > 0 ? "text-emerald-400" : saldoReisekasse < 0 ? "text-red-400" : "text-slate-400"
          }`}>
            {saldoReisekasse.toFixed(2)}
          </div>
        </div>
      </div>

      {saldoReisekasse === 0 && (
        <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4">
          ⚠️ In der Reisekasse ist aktuell kein Geld verfügbar.
        </div>
      )}

      {/* User balances */}
      {Object.entries(userTotals).some(([, { owed, paid }]) => owed - paid !== 0) && (
        <div className="space-y-1.5 pt-3 border-t border-[#2a3347]">
          {Object.entries(userTotals).map(([userId, { owed, paid }]) => {
            const balance = owed - paid;
            if (balance === 0) return null;

            const user = users.find((u) => u.id === userId);
            const name =
              user?.first_name || user?.last_name
                ? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()
                : user?.email ?? "Unbekannt";

            return (
              <div key={userId} className="flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {name} {balance > 0 ? "schuldet" : "bekommt"}
                </span>
                <span className={`text-xs font-medium tabular-nums ${balance > 0 ? "text-red-400" : "text-emerald-400"}`}>
                  {Math.abs(balance).toFixed(2)}
                </span>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-2 border-t border-[#1e2639]">
            <span className="text-[10px] text-slate-600">Gesamt offen</span>
            <span className={`text-xs font-semibold tabular-nums ${
              Object.values(userTotals).reduce((acc, { owed, paid }) => acc + (owed - paid), 0) >= 0
                ? "text-red-400"
                : "text-emerald-400"
            }`}>
              {Object.values(userTotals).reduce((acc, { owed, paid }) => acc + (owed - paid), 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
